import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Typography,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import {
  CloudUploadIcon,
  DeleteForeverIcon,
} from 'assets/constants/Icons';
import { getS3PresignedUrl } from 'lib/axiosUtils';
import {
  HTTP_HEADER_KEYS,
  HTTP_CONTENT_TYPES
} from 'assets/constants/apiConstants';

import { extractInvoiceNumber } from 'lib/invoiceExtractor';

const CommercialInvoicePanel = ({ invoiceData = {}, onUpdate, isReadOnly }) => {
  const [uploading, setUploading] = useState({ klin: false, other: false });
  const [uploadPct, setUploadPct] = useState({ klin: 0, other: 0 });
  const [dragActive, setDragActive] = useState({ klin: false, other: false });
  const [isScanning, setIsScanning] = useState(false);

  const latestData = useRef(invoiceData);
  useEffect(() => {
    latestData.current = invoiceData;
  }, [invoiceData]);

  const handleChange = (field, value) => {
    onUpdate({ ...latestData.current, [field]: value });
  };



  const handleFileUpload = async (files, type) => {
    if (!files || files.length === 0) return;

    let detectedInvoiceNo = null;
    if (type === 'klin' && files[0]) {
      detectedInvoiceNo = await extractInvoiceNumber(files[0], setIsScanning);
    }

    setUploading(prev => ({ ...prev, [type]: true }));
    setUploadPct(prev => ({ ...prev, [type]: 0 }));

    try {
      const newAttachments = [];
      const totalFiles = files.length;

      for (let i = 0; i < totalFiles; i++) {
        const file = files[i];
        
        const pres = await getS3PresignedUrl({}, {
          fileName: file.name,
          fileType: file.type || 'application/octet-stream', 
        });

        const { url: putUrl, fileUrl: publicUrl } = pres.data;

        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', putUrl, true);
          xhr.setRequestHeader(
            HTTP_HEADER_KEYS.CONTENT_TYPE,
            file.type || HTTP_CONTENT_TYPES.OCTET_STREAM
          );

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
              const fileProgress = (e.loaded / e.total);
              const totalProgress = Math.round(((i + fileProgress) / totalFiles) * 100);
              setUploadPct(prev => ({ ...prev, [type]: totalProgress }));
            }
          };

          xhr.onload = () =>
            xhr.status >= 200 && xhr.status < 300 ? resolve() : reject();
          xhr.onerror = () => reject();
          xhr.send(file);
        });

        let file_key = '';
        try {
          const u = new URL(putUrl);
          const path = decodeURIComponent(u.pathname.replace(/^\/+/, ''));
          const at = path.indexOf('uploads/');
          file_key = at >= 0 ? path.substring(at) : path;
        } catch (e) {}

        newAttachments.push({
          file_name: file.name,
          file_url: publicUrl,
          file_key,
          uploaded_at: new Date(),
        });
      }

      const fieldName = type === 'klin' ? 'kl_invoice_file' : 'other_commercial_attachments';
      const currentData = latestData.current[fieldName];
      let currentFiles = Array.isArray(currentData) ? currentData : (currentData ? [currentData] : []);

      const finalUpdates = {
        ...latestData.current,
        [fieldName]: [...currentFiles, ...newAttachments]
      };

      if (detectedInvoiceNo) {
        finalUpdates.klin_invoice_number = detectedInvoiceNo;
      }

      onUpdate(finalUpdates);

    } catch (err) {
      console.error('Upload failed', err);
      alert('Upload failed.');
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
      setUploadPct(prev => ({ ...prev, [type]: 0 }));
    }
  };

  const removeFile = (type, indexToRemove) => {
    const fieldName = type === 'klin' ? 'kl_invoice_file' : 'other_commercial_attachments';
    const currentData = latestData.current[fieldName];
    let currentFiles = Array.isArray(currentData) ? currentData : (currentData ? [currentData] : []);

    const updated = currentFiles.filter((_, index) => index !== indexToRemove);
    handleChange(fieldName, updated);
  };

  const handleDrag = (e, type, status) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isReadOnly && !uploading[type] && !isScanning) {
      setDragActive(prev => ({ ...prev, [type]: status }));
    }
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(prev => ({ ...prev, [type]: false }));
    if (isReadOnly || uploading[type] || isScanning) return;
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files, type);
    }
  };

  const renderTextField = (label, field, placeholder) => (
    <div className="col-md-6 mb-3">
      <label className={isReadOnly ? 'readonly-label form-label d-block bom-label' : 'form-label d-block bom-label'}>{label}</label>
      <input
        className={`form-control-customer bom-input ${isReadOnly ? 'readonly-input' : ''}`}
        value={invoiceData[field] || ''}
        placeholder={placeholder}
        disabled={isReadOnly}
        onChange={(e) => handleChange(field, e.target.value)}
      />
    </div>
  );

  const renderSelectField = (label, field, options) => (
    <div className="col-md-6 mb-3">
      <label className={isReadOnly ? 'readonly-label form-label d-block bom-label' : 'form-label d-block bom-label'}>
        {label}
      </label>
      <select
        className={`form-control-customer bom-input ${isReadOnly ? 'readonly-input' : ''}`}
        value={invoiceData[field] || 'Not Received'} 
        disabled={isReadOnly}
        onChange={(e) => handleChange(field, e.target.value)}
        style={{ appearance: 'auto', cursor: isReadOnly ? 'not-allowed' : 'pointer', backgroundColor: isReadOnly ? '#e9ecef' : '#fff' }} 
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );

  const renderDropZone = (label, type, fileData) => {
    const files = Array.isArray(fileData) ? fileData : (fileData ? [fileData] : []);
    const isDrag = dragActive[type];
    const isUp = uploading[type];
    const pct = uploadPct[type];
    
    const showScanning = type === 'klin' && isScanning;

    return (
      <div className="col-md-6 mb-3">
        <label className={isReadOnly ? 'readonly-label form-label d-block bom-label' : 'form-label d-block bom-label'}>{label}</label>

        {files.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {files.map((f, i) => (
              <Chip
                key={i}
                size="small"
                icon={<CloudUploadIcon />}
                label={f.file_name}
                onClick={() => window.open(f.file_url, '_blank')}
                onDelete={
                  !isReadOnly 
                    ? () => removeFile(type, i) 
                    : undefined
                }
                deleteIcon={<DeleteForeverIcon />}
                variant="outlined"
                title={f.file_name}
                sx={{
                    maxWidth: 200,
                    '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                }}
              />
            ))}
          </Box>
        )}

        {!isReadOnly && (
          <Box sx={{ mt: 0.5 }}>
            {showScanning ? (
              <Box sx={{ p: 2, textAlign: 'center', backgroundColor: '#fafafa', border: '2px dashed #ccc', borderRadius: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="textSecondary">Scanning file for Invoice Number...</Typography>
              </Box>
            ) : isUp ? (
              <Box sx={{ width: '100%', display:'flex', alignItems:'center', gap:1 }}>
                <LinearProgress variant="determinate" value={pct} sx={{flex:1}} />
                <Typography variant="caption">{pct}%</Typography>
              </Box>
            ) : (
              <Box
                onDragOver={(e) => handleDrag(e, type, true)}
                onDragEnter={(e) => handleDrag(e, type, true)}
                onDragLeave={(e) => handleDrag(e, type, false)}
                onDrop={(e) => handleDrop(e, type)}
                sx={{
                  border: '2px dashed',
                  borderColor: isDrag ? '#2d3d42' : '#ccc',
                  borderRadius: 1,
                  p: 2,
                  textAlign: 'center',
                  backgroundColor: isDrag ? '#f0f8ff' : '#fafafa',
                  transition: 'all 0.2s',
                  cursor: 'default',
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 1
                }}
              >
                <Typography variant="body2" color="textSecondary" sx={{fontSize: '12px'}}>
                  Drag & drop files here, or
                </Typography>

                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  startIcon={<CloudUploadIcon />}
                  sx={{ textTransform: 'none', borderColor: '#ccc', color: '#555', bgcolor: 'white' }}
                >
                  Browse Files
                  <input
                    type="file"
                    hidden
                    multiple 
                    // 2. REMOVED accept="application/pdf,image/*" to allow all types
                    onChange={(e) => handleFileUpload(e.target.files, type)}
                  />
                </Button>
              </Box>
            )}
          </Box>
        )}
      </div>
    );
  };

  return (
    <div className="row mt-4"> 
      <div className="col-12 mt-3 mb-2">
        <div className="bom-divider" />
        <Typography variant="subtitle2" sx={{ color: '#2d3d42', fontWeight: 600, mb: 1, fontSize: '15px !important' }}>
          Commercial Details
        </Typography>
      </div>

      {renderTextField(
        'KLIN Invoice Number',
        'klin_invoice_number',
        'e.g. INV-KL-001'
      )}

      {renderTextField(
        'Agreed Delivery Terms',
        'agreed_delivery_terms',
        'e.g. EXW, FOB, DDP'
      )}

      {renderDropZone(
        'KLIN Invoice Attachments',
        'klin',
        invoiceData.kl_invoice_file
      )}

      {renderDropZone(
        'Other Commercial Attachments',
        'other',
        invoiceData.other_commercial_attachments
      )}

      {renderSelectField(
        'Payment Status',
        'payment_status',
        ['Not Received', 'Received']
      )}

      {renderTextField(
        'Dispatch Comments',
        'dispatch_comments',
        'Enter any dispatch related comments...'
      )}
    </div>
  );
};

export default CommercialInvoicePanel;